import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const TRANSITIONS = [
  { id: 'fade', label: 'Fade', icon: 'Sunset' },
  { id: 'slide', label: 'Slide', icon: 'ArrowRight' },
  { id: 'zoom', label: 'Zoom', icon: 'ZoomIn' },
  { id: 'dissolve', label: 'Dissolve', icon: 'Layers' },
  { id: 'none', label: 'None', icon: 'Minus' },
];

const CAPTION_STYLES = [
  { id: 'subtitle', label: 'Subtitle', desc: 'Bottom centered text' },
  { id: 'title', label: 'Title Card', desc: 'Large centered overlay' },
  { id: 'lyric', label: 'Lyrics', desc: 'Animated lyric style' },
];

function generateScenes(game) {
  return [
    { id: 1, title: 'Opening Scene', duration: '0:00 - 0:08', thumbnail: game?.thumbnail, caption: '', transition: 'fade', selected: false },
    { id: 2, title: 'Main Action', duration: '0:08 - 0:18', thumbnail: game?.thumbnail, caption: '', transition: 'slide', selected: false },
    { id: 3, title: 'Climax', duration: '0:18 - 0:28', thumbnail: game?.thumbnail, caption: '', transition: 'zoom', selected: false },
    { id: 4, title: 'Outro', duration: '0:28 - 0:35', thumbnail: game?.thumbnail, caption: '', transition: 'fade', selected: false },
  ];
}

export default function VideoEditor({ game, onClose, onExport }) {
  const [scenes, setScenes] = useState(() => generateScenes(game));
  const [selectedScene, setSelectedScene] = useState(null);
  const [captionText, setCaptionText] = useState('');
  const [captionStyle, setCaptionStyle] = useState('subtitle');
  const [selectedTransition, setSelectedTransition] = useState('fade');
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(100);
  const [isExporting, setIsExporting] = useState(false);
  const [exportDone, setExportDone] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  const handleSelectScene = (scene) => {
    setSelectedScene(scene);
    setCaptionText(scene?.caption || '');
    setSelectedTransition(scene?.transition || 'fade');
  };

  const handleRemoveScene = (id) => {
    setScenes((prev) => prev?.filter((s) => s?.id !== id));
    if (selectedScene?.id === id) setSelectedScene(null);
  };

  const handleApplyCaption = () => {
    if (!selectedScene) return;
    setScenes((prev) => prev?.map((s) => s?.id === selectedScene?.id ? { ...s, caption: captionText } : s));
    setSelectedScene((prev) => prev ? { ...prev, caption: captionText } : null);
  };

  const handleApplyTransition = (t) => {
    setSelectedTransition(t);
    if (!selectedScene) return;
    setScenes((prev) => prev?.map((s) => s?.id === selectedScene?.id ? { ...s, transition: t } : s));
  };

  const handleMoveScene = (fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= scenes?.length) return;
    const updated = [...scenes];
    const [moved] = updated?.splice(fromIdx, 1);
    updated?.splice(toIdx, 0, moved);
    setScenes(updated);
  };

  const handleExport = () => {
    setIsExporting(true);
    setTimeout(() => {
      setIsExporting(false);
      setExportDone(true);
      setTimeout(() => {
        onExport?.({ ...game, scenes, trimStart, trimEnd, captionStyle });
      }, 1000);
    }, 2500);
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
    >
      <div
        className="w-full max-w-5xl max-h-[92vh] overflow-y-auto rounded-2xl flex flex-col"
        style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-xl)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.15)' }}>
              <Icon name="Scissors" size={16} color="var(--color-primary)" />
            </div>
            <div>
              <p className="text-sm font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Video Editor</p>
              <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{game?.title}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[var(--color-muted)] transition-all">
            <Icon name="X" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-0 flex-1 overflow-hidden">
          {/* Left: Scene Timeline */}
          <div className="lg:w-64 flex-shrink-0 border-r flex flex-col" style={{ borderColor: 'var(--color-border)' }}>
            <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--color-border)' }}>
              <p className="text-xs font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Scenes ({scenes?.length})</p>
            </div>
            <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2">
              {scenes?.map((scene, idx) => (
                <div
                  key={scene?.id}
                  className="relative rounded-xl overflow-hidden cursor-pointer border-2 transition-all group"
                  style={{
                    borderColor: selectedScene?.id === scene?.id ? 'var(--color-primary)' : 'var(--color-border)',
                    background: selectedScene?.id === scene?.id ? 'rgba(99,102,241,0.06)' : 'var(--color-muted)',
                  }}
                  onClick={() => handleSelectScene(scene)}
                >
                  <div className="flex items-center gap-2 p-2">
                    <div className="w-12 h-9 rounded-lg overflow-hidden flex-shrink-0">
                      <Image src={scene?.thumbnail} alt={scene?.title} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{scene?.title}</p>
                      <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{scene?.duration}</p>
                      {scene?.caption && (
                        <p className="text-xs truncate mt-0.5" style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-caption)' }}>📝 {scene?.caption}</p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={(e) => { e?.stopPropagation(); handleMoveScene(idx, idx - 1); }} className="w-5 h-5 rounded flex items-center justify-center hover:bg-[var(--color-border)]" disabled={idx === 0}>
                        <Icon name="ChevronUp" size={10} color="var(--color-muted-foreground)" />
                      </button>
                      <button onClick={(e) => { e?.stopPropagation(); handleMoveScene(idx, idx + 1); }} className="w-5 h-5 rounded flex items-center justify-center hover:bg-[var(--color-border)]" disabled={idx === scenes?.length - 1}>
                        <Icon name="ChevronDown" size={10} color="var(--color-muted-foreground)" />
                      </button>
                    </div>
                  </div>
                  {/* Transition badge */}
                  <div className="absolute bottom-1 right-1">
                    <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: 'rgba(99,102,241,0.15)', color: 'var(--color-primary)', fontFamily: 'var(--font-caption)', fontSize: 9 }}>
                      {scene?.transition}
                    </span>
                  </div>
                  {/* Remove button */}
                  <button
                    onClick={(e) => { e?.stopPropagation(); handleRemoveScene(scene?.id); }}
                    className="absolute top-1 right-1 w-5 h-5 rounded flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ background: 'rgba(239,68,68,0.15)' }}
                  >
                    <Icon name="Trash2" size={9} color="var(--color-error)" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Center: Preview + Controls */}
          <div className="flex-1 flex flex-col">
            {/* Preview */}
            <div className="relative flex-shrink-0" style={{ background: '#0a0a0f', height: 220 }}>
              {selectedScene ? (
                <>
                  <Image src={selectedScene?.thumbnail} alt={selectedScene?.title} className="w-full h-full object-cover opacity-70" />
                  {selectedScene?.caption && (
                    <div className="absolute bottom-4 left-0 right-0 flex justify-center">
                      <span
                        className="px-4 py-1.5 rounded-lg text-sm font-semibold text-white"
                        style={{ background: 'rgba(0,0,0,0.75)', fontFamily: 'var(--font-body)', backdropFilter: 'blur(4px)' }}
                      >
                        {selectedScene?.caption}
                      </span>
                    </div>
                  )}
                  <div className="absolute top-2 left-2">
                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: 'rgba(99,102,241,0.85)', color: 'white', fontFamily: 'var(--font-caption)' }}>
                      {selectedScene?.title}
                    </span>
                  </div>
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <Icon name="Film" size={32} color="rgba(255,255,255,0.2)" />
                    <p className="text-xs mt-2" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'var(--font-caption)' }}>Select a scene to edit</p>
                  </div>
                </div>
              )}
            </div>

            {/* Edit Controls */}
            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
              {/* Trim */}
              <div>
                <p className="text-xs font-bold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>✂️ Trim Video</p>
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-3">
                    <span className="text-xs w-16" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Start: {trimStart}%</span>
                    <input type="range" min={0} max={trimEnd - 5} value={trimStart} onChange={(e) => setTrimStart(Number(e?.target?.value))} className="flex-1 accent-[var(--color-primary)]" />
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs w-16" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>End: {trimEnd}%</span>
                    <input type="range" min={trimStart + 5} max={100} value={trimEnd} onChange={(e) => setTrimEnd(Number(e?.target?.value))} className="flex-1 accent-[var(--color-primary)]" />
                  </div>
                  {/* Visual trim bar */}
                  <div className="relative h-6 rounded-lg overflow-hidden" style={{ background: 'var(--color-muted)' }}>
                    <div
                      className="absolute top-0 bottom-0 rounded-lg"
                      style={{ left: `${trimStart}%`, right: `${100 - trimEnd}%`, background: 'linear-gradient(90deg, var(--color-primary), var(--color-secondary))' }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xs font-semibold text-white" style={{ fontFamily: 'var(--font-data)' }}>
                        {trimEnd - trimStart}% selected
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Transitions */}
              <div>
                <p className="text-xs font-bold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>🎬 Transitions</p>
                <div className="flex gap-2 flex-wrap">
                  {TRANSITIONS?.map((t) => (
                    <button
                      key={t?.id}
                      onClick={() => handleApplyTransition(t?.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs transition-all"
                      style={{
                        borderColor: selectedTransition === t?.id ? 'var(--color-primary)' : 'var(--color-border)',
                        background: selectedTransition === t?.id ? 'rgba(99,102,241,0.1)' : 'var(--color-muted)',
                        color: selectedTransition === t?.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)',
                        fontFamily: 'var(--font-caption)',
                      }}
                    >
                      <Icon name={t?.icon} size={11} color={selectedTransition === t?.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} />
                      {t?.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Captions */}
              <div>
                <p className="text-xs font-bold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>📝 Captions / Lyrics</p>
                <div className="flex gap-2 mb-2">
                  {CAPTION_STYLES?.map((cs) => (
                    <button
                      key={cs?.id}
                      onClick={() => setCaptionStyle(cs?.id)}
                      className="flex-1 text-xs py-1.5 rounded-lg border transition-all"
                      style={{
                        borderColor: captionStyle === cs?.id ? 'var(--color-primary)' : 'var(--color-border)',
                        background: captionStyle === cs?.id ? 'rgba(99,102,241,0.1)' : 'var(--color-muted)',
                        color: captionStyle === cs?.id ? 'var(--color-primary)' : 'var(--color-muted-foreground)',
                        fontFamily: 'var(--font-caption)',
                      }}
                    >
                      {cs?.label}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={captionText}
                    onChange={(e) => setCaptionText(e?.target?.value)}
                    placeholder={selectedScene ? 'Add caption to selected scene...' : 'Select a scene first'}
                    disabled={!selectedScene}
                    className="flex-1 px-3 py-2 text-xs rounded-xl border focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                    style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)', color: 'var(--color-foreground)', fontFamily: 'var(--font-body)' }}
                  />
                  <button
                    onClick={handleApplyCaption}
                    disabled={!selectedScene}
                    className="px-3 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-80 disabled:opacity-40"
                    style={{ background: 'var(--color-primary)', color: 'white', fontFamily: 'var(--font-caption)' }}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t flex-shrink-0" style={{ borderColor: 'var(--color-border)' }}>
          <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
            {scenes?.length} scenes · Trim {trimStart}% – {trimEnd}%
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-xs font-semibold border transition-all hover:opacity-80"
              style={{ borderColor: 'var(--color-border)', color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              disabled={isExporting || exportDone}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-90 disabled:opacity-70"
              style={{ background: exportDone ? 'var(--color-success)' : 'var(--color-primary)', color: 'white', fontFamily: 'var(--font-heading)' }}
            >
              {isExporting ? (
                <><Icon name="Loader" size={13} color="white" /> Exporting...</>
              ) : exportDone ? (
                <><Icon name="CheckCircle2" size={13} color="white" /> Exported!</>
              ) : (
                <><Icon name="Download" size={13} color="white" /> Export Video</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
